import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../firebase';
import LoadingLockScreen from './LoadingLockScreen';
import { openExternalLinkReuseOrFocus, openExternalLinkNewTab } from '../lib/externalLinkWindows';
import { appendTicketLog } from '../lib/ticketLogs';

function getAssignedIds(link) {
  return Array.isArray(link.assignedUsers) ? link.assignedUsers : [];
}

function firestoreLinksErrorMessage(err) {
  const msg = err?.message || '';
  if (msg.includes('building') || err?.code === 'failed-precondition') {
    return 'Firestore is still building a required index (often a few minutes). Refresh shortly, or check Indexes in the Firebase console.';
  }
  if (msg.includes('index')) {
    return 'This query needs a Firestore composite index. Use the link from the error in the console, or deploy firestore.indexes.json.';
  }
  return msg || 'Could not load links.';
}

function parseDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function healthChip(link) {
  const hb = parseDate(link.heartbeatAt);
  const status = link.liveStatus || 'unknown';
  if (status === 'down') return { label: 'DOWN', color: 'error' };
  if (status === 'degraded') return { label: 'DEGRADED', color: 'warning' };
  if (status === 'live') {
    if (!hb) return { label: 'LIVE', color: 'success' };
    const tenMinutes = 10 * 60 * 1000;
    if (Date.now() - hb.getTime() <= tenMinutes) return { label: 'LIVE', color: 'success' };
    return { label: 'STALE', color: 'warning' };
  }
  return { label: 'UNKNOWN', color: 'default' };
}

const AdminOverviewExternalLinks = ({ userData, onManageLinks }) => {
  const [links, setLinks] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [splashOnce, setSplashOnce] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [checkingLinkId, setCheckingLinkId] = useState('');

  const companyId = userData?.companyId;
  const isCompanyAdmin = userData?.role === 'company_admin';

  const load = useCallback(async () => {
    if (!companyId || !isCompanyAdmin) {
      setLinks([]);
      setCompanyUsers([]);
      setSplashOnce(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [linksSnap, usersSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, 'links'),
            where('companyId', '==', companyId),
            orderBy('createdAt', 'desc')
          )
        ),
        getDocs(query(collection(db, 'users'), where('companyId', '==', companyId))),
      ]);

      setLinks(
        linksSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );

      const users = usersSnap.docs.map((d) => {
        const data = d.data();
        return {
          uid: d.id,
          displayName: data.displayName,
          email: data.email,
        };
      });
      users.sort((a, b) =>
        (a.displayName || a.email || a.uid).localeCompare(b.displayName || b.email || b.uid)
      );
      setCompanyUsers(users);
    } catch (e) {
      console.error('Admin overview links load error:', e);
      setLoadError(firestoreLinksErrorMessage(e));
      setLinks([]);
      setCompanyUsers([]);
    } finally {
      setLoading(false);
      setSplashOnce(false);
    }
  }, [companyId, isCompanyAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCheckLinkNow = async (linkId) => {
    if (!linkId) return;
    setCheckingLinkId(linkId);
    try {
      const runCheck = httpsCallable(functions, 'runExternalLinkLivenessNow');
      await runCheck({ linkId });
      const link = links.find((l) => l.id === linkId);
      const actor = auth.currentUser;
      if (actor && companyId) {
        await appendTicketLog(db, {
          companyId,
          ticketId: null,
          action: 'external_link_liveness_run',
          summary: link ? `Liveness check: ${link.title || link.url || linkId}` : 'Liveness check run',
          meta: { linkId, title: link?.title || null },
          actorUid: actor.uid,
          actorEmail: actor.email || '',
          actorRole: userData?.role || '',
          participantUids: [actor.uid],
          resourceType: 'link',
        });
      }
      await load();
    } catch (e) {
      console.error('runExternalLinkLivenessNow:', e);
      setLoadError(e.message || 'Could not run liveness check.');
    } finally {
      setCheckingLinkId('');
    }
  };

  if (!isCompanyAdmin || !companyId) {
    return null;
  }

  const byUid = new Map(companyUsers.map((u) => [u.uid, u]));

  return (
    <Box sx={{ mt: 4 }}>
      <LoadingLockScreen open={loading && splashOnce} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LinkIcon color="primary" />
          <Typography variant="h6" component="h2">
            External links
          </Typography>
        </Stack>
        <Button size="small" variant="outlined" onClick={onManageLinks}>
          Manage links
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Snapshot of company links and who can see them. Opens the same data as the External Links tab.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      {loading && !splashOnce ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !loading && links.length === 0 && !loadError ? (
        <Typography variant="body2" color="text.secondary">
          No links yet. Use Manage links to add some.
        </Typography>
      ) : !loading && loadError ? null : !loading ? (
        <List
          dense
          disablePadding
          sx={{
            maxHeight: 360,
            overflow: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          {links.map((link) => {
            const ids = getAssignedIds(link);
            const hc = healthChip(link);
            return (
              <ListItem
                key={link.id}
                divider
                alignItems="flex-start"
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Open or focus (reuses tab if you opened this link from Shifty before)">
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={`Open or focus: ${link.title}`}
                        onClick={() => openExternalLinkReuseOrFocus(link.id, link.url)}
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="New tab">
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={`New tab: ${link.title}`}
                        onClick={() => openExternalLinkNewTab(link.url)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Run liveness check now">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          disabled={checkingLinkId === link.id}
                          onClick={() => handleCheckLinkNow(link.id)}
                        >
                          {checkingLinkId === link.id ? <CircularProgress size={14} /> : <AutorenewIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                }
                sx={{ alignItems: 'flex-start', py: 1 }}
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Typography
                      component="div"
                      variant="subtitle2"
                      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', m: 0 }}
                    >
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {link.title}
                      </Box>
                      <Chip size="small" label={link.category || 'Uncategorized'} variant="outlined" />
                      <Chip size="small" label={hc.label} color={hc.color} variant="outlined" />
                      <Chip
                        size="small"
                        color={ids.length ? 'primary' : 'default'}
                        variant={ids.length ? 'filled' : 'outlined'}
                        label={ids.length === 0 ? 'Unassigned' : `${ids.length} assigned`}
                      />
                    </Typography>
                  }
                  secondary={
                    <Typography
                      component="div"
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 0.5, m: 0 }}
                    >
                      <Box component="span" sx={{ width: '100%' }}>
                        {link.lastCheckAt ? `Checked ${parseDate(link.lastCheckAt)?.toLocaleString() || '—'}` : 'Never checked'}
                      </Box>
                      {ids.length === 0 ? (
                        'Admins only — add agents in External Links.'
                      ) : (
                        ids.map((uid) => {
                          const u = byUid.get(uid);
                          const label = u ? u.displayName || u.email || uid : uid;
                          return (
                            <Chip key={uid} size="small" variant="outlined" label={label} sx={{ height: 22 }} />
                          );
                        })
                      )}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      ) : null}
    </Box>
  );
};

export default AdminOverviewExternalLinks;
